import {
  NotificationFormatInput,
  NotificationFormatter,
  Notifications,
  NotificationTypes,
} from '../models/notifications';

export interface NotificationTemplate {
  title: string;
  body: string;
  actionLabel?: string;
}

export const LEAD_STATUS_LABELS: Record<number, string> = {
  1: 'Under review',
  2: 'Further information required',
  3: 'Prospective client',
  4: 'On hold',
  5: 'Declined',
  6: 'Not currently pursuing',
  7: 'Active client',
  8: 'Request lead contact',
  9: 'Active Funded',
  10: 'Active Funded Grant',
  11: 'Active Technical Assistance',
};

export const NOTIFICATION_TEMPLATES: Record<number, NotificationTemplate> = {
  [NotificationTypes.SHARE_RECORD]: {
    title: 'Business Shared',
    body: '{user} has shared a business with you.',
    actionLabel: 'View Record',
  },
  [NotificationTypes.CDFI_OUTREACH_GLOBAL]: {
    title: 'Business Outreach Inquiry',
    body: 'Business {business} has reached out with an inquiry.',
    actionLabel: 'View Inquiry',
  },
  [NotificationTypes.CDFI_OUTREACH_GENERAL_MATCHED]: {
    title: 'Lead Match Found',
    body: '{business} matches your lead criteria.',
    actionLabel: 'View Details',
  },
  [NotificationTypes.CDFI_OUTREACH_LEAD]: {
    title: 'Lead Outreach Submitted',
    body: 'Your lead {business} has submitted a new outreach form.',
    actionLabel: 'View Details',
  },
  [NotificationTypes.CDFI_OUTREACH_LOAN_FORM]: {
    title: 'Loan Details Submitted',
    body: 'Your lead {business} has submitted the Loan details form.',
    actionLabel: 'View Details',
  },
  [NotificationTypes.SHARE_RECORD_NOTE]: {
    title: 'Business Note Shared',
    body: 'A note has been shared for the business {business} by {user} in {organization}.',
    actionLabel: 'View Note',
  },
  [NotificationTypes.SHARE_DOCUMENT_TO_USER]: {
    title: 'Document Shared with You',
    body: 'A file {file_name} has been shared to you by {user} in {organization}.',
    actionLabel: 'View Files',
  },
  [NotificationTypes.SHARE_DOCUMENT_TO_CONNECTION]: {
    title: 'Document Shared with Connection',
    body: 'A file {file_name} has been shared to your connection by {user} in {organization}.',
    actionLabel: 'View Files',
  },
  [NotificationTypes.ORG_SELF_SIGNUP]: {
    title: 'Organization Self-Signup Request',
    body: '{organization} has submitted a self-signup request.',
    actionLabel: 'Review Request',
  },
  [NotificationTypes.RECORD_VISIBILITY_UPDATED]: {
    title: 'Record Visibility Updated',
    body: '{user} of {organization} changed visibility of {business} to {new_visibility}. {access_clause}',
    actionLabel: 'View Details',
  },
  [NotificationTypes.PDM_SHARE_APPROVAL]: {
    title: 'File Share Approval Required',
    body: "New file '{file_name}' requires approval for sharing.",
    actionLabel: 'Review & Approve',
  },
  [NotificationTypes.FILE_MALICIOUS_DETECTED]: {
    title: 'Security Alert: Malicious File',
    body: 'The uploaded file {file_name} was detected as malicious and has been blocked for security reasons.',
    actionLabel: 'View Files',
  },
  [NotificationTypes.SHARE_LEAD_FILE]: {
    title: 'Lead Record File Shared',
    body: '{user} from {organization} shared a file in {business}.',
    actionLabel: 'View Details',
  },
  [NotificationTypes.LEAD_BUSINESS_UPDATED]: {
    title: 'Lead Business Details Updated',
    body: "Your lead {business} has updated it's details.",
    actionLabel: 'View Details',
  },
  [NotificationTypes.LEAD_BUSINESS_DELETED]: {
    title: 'Lead Business Deleted',
    body: 'Your lead {business} has been deleted.',
    actionLabel: 'View Info',
  },
  [NotificationTypes.CDFI_PROFILE_VIEW]: {
    title: 'SME Profile Viewed',
    body: 'SME {user} has viewed your profile.',
    actionLabel: 'View Profile',
  },
  [NotificationTypes.USER_PROFILE_VIEW]: {
    title: 'Profile Viewed',
    body: '{user} from {organization} has viewed your profile.',
    actionLabel: 'View CDFI',
  },
  [NotificationTypes.SME_LEAD_ASSIGNED]: {
    title: 'Lead Assigned',
    body: 'Your business {business} has been assigned by {organization}.',
    actionLabel: 'View Details',
  },
  [NotificationTypes.SME_LEAD_STATUS_CHANGED]: {
    title: 'Lead Status Updated',
    body: 'The lead status for your business {business} has been updated to {lead_status} by {organization}.',
    actionLabel: 'View Details',
  },
  [NotificationTypes.VLF_RECORD_CREATED]: {
    title: 'New VLF Submission Received',
    body: 'A new VLF submission for {business} has been received. Claim before it expires.',
    actionLabel: 'View Lead',
  },
  [NotificationTypes.VLF_LEAD_CLAIMED]: {
    title: 'VLF Lead Claimed',
    body: 'VLF lead for {business} has been claimed by {organization}.',
    actionLabel: 'View Lead',
  },
  [NotificationTypes.VLF_LEAD_STATUS_UPDATED]: {
    title: 'VLF Lead Status Updated',
    body: 'VLF lead status for {business} updated to {lead_status}.',
    actionLabel: 'View Lead',
  },
  [NotificationTypes.VLF_LEAD_WEEKLY_REMINDER]: {
    title: 'VLF Lead Status Reminder',
    body: 'Please provide a status update for VLF lead {business}.',
    actionLabel: 'Update Status',
  },
  [NotificationTypes.VLF_LEAD_UNFUNDED_REALLOCATED]: {
    title: 'VLF Lead Reallocated',
    body: 'VLF lead for {business} was not funded within 7 business days and is now available for claim.',
    actionLabel: 'View Lead',
  },
  [NotificationTypes.VLF_LEAD_TIMEOUT_INFORM_VLF]: {
    title: 'VLF Lead Unclaimed',
    body: 'No DH organization claimed VLF lead for {business} within 3 business days.',
    actionLabel: 'View Info',
  },
  [NotificationTypes.VLF_LEAD_FUNDING_EXPIRY_REMINDER]: {
    title: 'VLF Funding Deadline Warning',
    body: 'You have {days_remaining} business day(s) left to fund VLF lead {business} before it is reallocated.',
    actionLabel: 'View Lead',
  },
  [NotificationTypes.VLF_LEAD_FUNDING_EXPIRED_REALLOCATED]: {
    title: 'VLF Lead Unassigned (Funding Expired)',
    body: 'Your claim on VLF lead {business} has expired because it was not funded within 7 business days. The lead has been unassigned and reallocated.',
    actionLabel: 'View Info',
  },
};

export const SYSTEM_NOTIFICATION_TEMPLATES: Record<
  number,
  NotificationTemplate
> = {
  [NotificationTypes.SHARE_DOCUMENT_TO_USER]: {
    title: 'Document Shared with You',
    body: 'A file {file_name} has been shared to you by PDM team.',
    actionLabel: 'View Files',
  },
  [NotificationTypes.SHARE_DOCUMENT_TO_CONNECTION]: {
    title: 'Document Shared with Connection',
    body: 'A file {file_name} has been shared to your connection by PDM team.',
    actionLabel: 'View Files',
  },
};

/**
 * Formats a notification message dynamically by replacing placeholders in its template
 * with the corresponding metadata from `message_data`.
 */
export function formatNotification(
  notification: Notifications | NotificationFormatInput,
): NotificationFormatter {
  const type = notification.notification_type;
  const data = notification.message_data || {};

  if (type === NotificationTypes.ANNOUNCEMENT) {
    return {
      title: 'Admin Announcement',
      body: String(data['announcement_subject'] || 'Announcement from Admin'),
      actionLabel: 'Read Announcement',
    };
  }

  let templateObj = NOTIFICATION_TEMPLATES[type];
  if (data['is_system'] && SYSTEM_NOTIFICATION_TEMPLATES[type]) {
    templateObj = SYSTEM_NOTIFICATION_TEMPLATES[type]!;
  }

  if (!templateObj) {
    return {
      title: 'New Notification',
      body: 'Click here to view details.',
      actionLabel: '',
    };
  }

  let body = templateObj.body;
  let title = templateObj.title;

  const orgName =
    data['organization'] ||
    data['connection'] ||
    data['cdfi'] ||
    'Organization';

  let leadStatus = '';
  if (data['lead_status'] !== undefined && data['lead_status'] !== null) {
    const statusVal = Number(data['lead_status']);
    leadStatus = LEAD_STATUS_LABELS[statusVal] || 'Under review';
  }

  body = body
    .replace(/{user}/g, String(data['user'] || 'User'))
    .replace(/{business}/g, String(data['business'] || 'Business'))
    .replace(/{file_name}/g, String(data['file_name'] || 'Document'))
    .replace(/{organization}/g, String(orgName))
    .replace(/{new_visibility}/g, String(data['new_visibility'] || ''))
    .replace(/{access_clause}/g, String(data['access_clause'] || ''))
    .replace(/{days_remaining}/g, String(data['days_remaining'] || ''))
    .replace(/{lead_status}/g, leadStatus);

  title = title
    .replace(/{user}/g, String(data['user'] || 'User'))
    .replace(/{business}/g, String(data['business'] || 'Business'))
    .replace(/{file_name}/g, String(data['file_name'] || 'Document'))
    .replace(/{organization}/g, String(orgName))
    .replace(/{lead_status}/g, leadStatus);

  return {
    title: title.replace(/\s+/g, ' ').trim(),
    body: body.replace(/\s+/g, ' ').trim(),
    actionLabel: templateObj.actionLabel,
  };
}
